// Vendors
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createAccountStart } from '@/store/features/account/accountSlice';
import {
  Alert,
  Box,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  InputLabel,
  Snackbar,
  SelectChangeEvent,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Assets
import registerLogo from '@assets/images/signin-logo.svg';
import { useAppSelector } from '@/store/store';
import Loader from '@/components/atoms/Loader';
import { useNavigate } from 'react-router-dom';
import PasswordInfoIcon from '@/components/atoms/InfoIcon';
import SelectComponent from '@/components/atoms/Select';

export const CreateAccount: React.FC = () => {
  const dispatch = useDispatch();
  const { loading, success, error, errorMessage } = useAppSelector((state) => state.account);
  const navigate = useNavigate();

  const [isSuccess, setSuccess] = useState<boolean | null>(null);
  const [message, setMessage] = useState<string | null>('');
  const [showMessage, setOpen] = useState(false);

  const initialPasswordCriteria = [
    {
      condition: '8+ characters',
      check: (val: string) => val.length >= 8,
    },
    {
      condition: 'uppercase letter',
      check: (val: string) => /[A-Z]/.test(val),
    },
    {
      condition: 'lowercase letter',
      check: (val: string) => /[a-z]/.test(val),
    },
    {
      condition: 'at least one number',
      check: (val: string) => /\d/.test(val),
    },
    {
      condition: 'at least one symbol',
      check: (val: string) => /^(?=.*[^A-Za-z0-9]).+$/.test(val),
    },
  ];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    customerId: '',
    zipCode: '',
    email: '',
    password: '',
    question: '',
    answer: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (success) {
      setOpen(true);
      setSuccess(success);
      setMessage('User created Successfully!');
      setFormData({
        firstName: '',
        lastName: '',
        customerId: '',
        zipCode: '',
        email: '',
        password: '',
        question: '',
        answer: '',
      });
      setTimeout(() => {
        setMessage('');
        void navigate('/login');
      }, 2000);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      setOpen(true);
      setSuccess(!error);
      setMessage(errorMessage);
    }
  }, [error]);

  const validateField = (name: string, value: string) => {
    let error = '';
    switch (name) {
      case 'firstName':
        if (!value) error = 'Please enter a First Name';
        break;
      case 'lastName':
        if (!value) error = 'Please enter a Last Name';
        break;
      case 'customerId':
        if (!value) error = 'Please enter a Customer Number';
        else if (!/^\d{6}-\d{5}$/.test(value)) error = 'Invalid Customer Number';
        break;
      case 'zipCode':
        if (!value) error = 'Please enter a Zipcode';
        else if (!/^\d{5}$/.test(value)) error = 'Invalid Zipcode';
        break;
      case 'email':
        if (!value) error = 'Please enter an Email';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Incorrect Email';
        break;
      case 'question':
        if (!value) error = 'Please select a recovery question';
        break;
      case 'answer':
        if (!value) error = 'Please enter a answer';
        break;
      case 'password':
        if (!value) error = 'Please enter a Password';
        else if (initialPasswordCriteria.some((item) => !item.check(value))) {
          error = 'Invalid Password';
        }
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    /* eslint-disable @typescript-eslint/no-unnecessary-type-conversion */
    setFormData((prev) => ({ ...prev, [name]: String(value).trimStart() }));
    validateField(name, value);
  };

  const handleSelectChange = (e: SelectChangeEvent<string | null>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: String(value).trimStart() }));
    if (value) validateField(name, value);
  };

  const handleCustomerIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/[^\d-]/g, '');
    if (value.length > 6) {
      value = value.slice(0, 6) + '-' + value.slice(6).replace(/-/g, '');
    }
    if (value.length > 12) {
      value = value.slice(0, 12);
    }
    setFormData((prev) => ({ ...prev, customerId: value }));
    validateField('customerId', value);
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName) {
      newErrors.firstName = 'Please enter a First Name';
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Please enter a Last Name';
    }

    if (!formData.customerId) {
      newErrors.customerId = 'Please enter a Customer Number';
    } else if (!/^\d{6}-\d{5}$/.test(formData.customerId)) {
      newErrors.customerId = 'Invalid Customer Number';
    }

    if (!formData.zipCode) {
      newErrors.zipCode = 'Please enter a Zipcode';
    } else if (!/^\d{5}$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Invalid Zipcode';
    }

    if (!formData.email) {
      newErrors.email = 'Please enter an Email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Incorrect Email';
    }

    if (!formData.password) {
      newErrors.password = 'Please enter a Password';
    } else if (initialPasswordCriteria.some((item) => !item.check(formData.password))) {
      newErrors.password = 'Invalid Password';
    }

    if (!formData.question) {
      newErrors.question = 'Please select a recovery question';
    }

    if (!formData.answer) {
      newErrors.answer = 'Please enter recovery question answer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validate()) {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        customerNumber: formData.customerId,
        zipCode: formData.zipCode,
        recovery: {
          question: formData.question,
          answer: formData.answer.trim(),
        },
      };
      dispatch(createAccountStart(payload));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} className="register__wrapper">
      <Loader display={loading} />
      <Snackbar
        open={showMessage}
        autoHideDuration={3000}
        onClose={() => {
          setOpen(false);
          setMessage('');
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <div>
          <Alert
            severity={isSuccess ? 'success' : 'error'}
            variant="filled"
            onClose={() => {
              setOpen(false);
              setMessage('');
            }}
          >
            {message}
          </Alert>
        </div>
      </Snackbar>
      <div className="title_wrap">
        <img src={registerLogo} alt="logo" className="logo" />
        <Typography className="title">Register</Typography>
      </div>

      <div className="field-row_wrap">
        <div className="field_wrap">
          <InputLabel className="label">First Name</InputLabel>
          <TextField
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
              },
            }}
          />
        </div>
        <div className="field_wrap">
          <InputLabel className="label">Last Name</InputLabel>
          <TextField
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
              },
            }}
          />
        </div>
      </div>

      <div className="field-row_wrap">
        <div className="field_wrap">
          <InputLabel className="label">Customer #</InputLabel>
          <TextField
            name="customerId"
            value={formData.customerId}
            onChange={handleCustomerIdChange}
            placeholder="000000-00000"
            error={!!errors.customerId}
            helperText={errors.customerId}
            slotProps={{
              input: {
                inputProps: { maxLength: 12 },
              },
            }}
          />
        </div>
        <div className="field_wrap">
          <InputLabel className="label">Zip Code</InputLabel>
          <TextField
            name="zipCode"
            placeholder="Zip Code"
            value={formData.zipCode}
            onChange={handleChange}
            error={!!errors.zipCode}
            helperText={errors.zipCode}
            slotProps={{
              input: {
                inputProps: { maxLength: 5 },
              },
            }}
          />
        </div>
      </div>

      <div className="field_wrap">
        <InputLabel className="label">Email Address</InputLabel>
        <TextField
          name="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
        />
      </div>

      <div className="field_wrap pt-1">
        <InputLabel className="label">
          Password
          <PasswordInfoIcon />
        </InputLabel>
        <TextField
          name="password"
          placeholder="Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          error={!!errors.password}
          helperText={errors.password}
          autoComplete="new-password"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& input::-ms-reveal': { display: 'none' },
            '& input::-ms-clear': { display: 'none' },
          }}
        />
      </div>

      <div className="field_wrap pt-1">
        <InputLabel className="label">Recovery Question</InputLabel>
        <SelectComponent
          handleChange={handleSelectChange}
          question={formData.question}
          error={errors}
        />
      </div>

      {formData.question && formData.question != '' && (
        <div className="field_wrap pt-1">
          <InputLabel className="label">Recovery Question Answer</InputLabel>
          <TextField
            name="answer"
            placeholder="Recovery Question Answer"
            value={formData.answer}
            onChange={handleChange}
            error={!!errors.answer}
            helperText={errors.answer}
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
              },
            }}
          />
        </div>
      )}

      <Button type="submit" className="reg-btn" variant="contained" color="primary">
        REGISTER
      </Button>

      <Typography className="account-desc" mt={2}>
        Already have an account? <a href="/login">Login</a>
      </Typography>
    </Box>
  );
};

export default CreateAccount;
